import type { ITabOption } from '@src/components/atoms/tabs';
import Tabs from '@src/components/atoms/tabs';
import ActiveDRepDelegators from '@src/components/molecules/ActiveDRepDelegators';
import DRepMetadata from '@src/components/molecules/DRepMetadata';
import DRepRegistrations from '@src/components/molecules/DRepRegistrations';
import DRepVotes from '@src/components/molecules/DRepVotes';
import {
  useGetDRepDelegationsQuery,
  useGetActiveDRepDelegatorsQuery,
  useGetDRepRegistrationsQuery,
  useGetDRepVotesQuery,
} from '@src/store/drep/api';
import { useEffect, useState } from 'react';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { MdOutlineHowToVote, MdPeopleOutline } from 'react-icons/md';
import { IoKeyOutline } from 'react-icons/io5';
import type { IMetadata } from '@src/models/dtos/metadata';
import { useRouter } from 'next/navigation';
import {
  readSearchParam,
  readSearchParams,
} from '@src/utils/searchParamsUtils';
import DelegationsIcon from '@src/components/icons/delegationsIcon';
import Delegations from '@src/components/molecules/Delegations';
import { getContentState } from '@src/utils/queryStateUtils';
import type { SeoVotes } from '@src/lib/seo/votes';

interface DRepProfileTabsProps {
  queryDrepId: string | undefined;
  isOwner: boolean;
  metadata: IMetadata | null;
  seoVotes?: SeoVotes;
}
export default function DrepProfileTabs({
  queryDrepId,
  isOwner,
  metadata,
  seoVotes,
}: DRepProfileTabsProps) {
  const router = useRouter();

  const dRepTabs: ITabOption[] = [
    {
      value: 'info',
      label: 'DRep info',
      icon: IoIosInformationCircleOutline,
      tabClassName: 'rounded-tl-lg min-w-[117px]',
    },
    {
      value: 'votes',
      label: 'Votes',
      icon: MdOutlineHowToVote,
    },
    {
      value: 'registrations',
      label: 'Registrations',
      icon: IoKeyOutline,
      tabClassName: 'md:rounded-none rounded-tr-lg',
    },
    {
      value: 'active-delegators',
      label: 'Active Delegators',
      icon: MdPeopleOutline,
      tabClassName: 'min-w-[165px]',
    },
    {
      value: 'delegations',
      label: 'Delegations',
      icon: DelegationsIcon,
    },
  ];

  const [activeTab, setActiveTab] = useState<ITabOption>(dRepTabs[0]);

  const [paginationState, setPaginationState] = useState({
    votesPage: 1,
    activeDelegatorsPage: 1,
    registrationsPage: 1,
    delegationsPage: 1,
  });

  const dRepVotesQuery = useGetDRepVotesQuery(
    {
      dRepId: queryDrepId || '',
      params: { page: paginationState.votesPage, size: 10 },
    },
    {
      skip: queryDrepId === undefined || queryDrepId === null,
    }
  );

  const dRepRegistrationsQuery = useGetDRepRegistrationsQuery(
    {
      dRepId: queryDrepId || '',
      params: {
        page: paginationState.registrationsPage,
        size: 10,
      },
    },
    {
      skip: queryDrepId === undefined || queryDrepId === null,
    }
  );

  const activeDRepDelegatorsListQuery = useGetActiveDRepDelegatorsQuery(
    {
      dRepId: queryDrepId || '',
      params: {
        page: paginationState.activeDelegatorsPage,
        size: 10,
      },
    },
    {
      skip: queryDrepId === undefined || queryDrepId === null,
    }
  );

  const dRepDelegationsQuery = useGetDRepDelegationsQuery(
    {
      dRepId: queryDrepId || '',
      params: {
        page: paginationState.delegationsPage,
        size: 10,
      },
    },
    {
      skip: queryDrepId === undefined || queryDrepId === null,
    }
  );

  const dRepVotes = dRepVotesQuery.data;
  const dRepRegistrations = dRepRegistrationsQuery.data;
  const ActiveDRepDelegatorsList = activeDRepDelegatorsListQuery.data;
  const dRepDelegations = dRepDelegationsQuery.data;

  type PaginationKey = keyof typeof paginationState;

  const handlePageForward = (key: PaginationKey) => {
    let totalCount = 0;
    let size = 10;

    switch (key) {
      case 'votesPage':
        totalCount = dRepVotes?.totalCount || 0;
        size = dRepVotes?.size || 10;
        break;
      case 'activeDelegatorsPage':
        totalCount = ActiveDRepDelegatorsList?.totalCount || 0;
        size = ActiveDRepDelegatorsList?.size || 10;
        break;
      case 'registrationsPage':
        totalCount = dRepRegistrations?.totalCount || 0;
        size = dRepRegistrations?.size || 10;
        break;
      case 'delegationsPage':
        totalCount = dRepDelegations?.totalCount || 0;
        size = dRepDelegations?.size || 10;
        break;
    }

    const maxPage = Math.ceil(totalCount / size);
    if (paginationState[key] < maxPage) {
      setPaginationState((prev) => ({
        ...prev,
        [key]: prev[key] + 1,
      }));
    }
  };

  const handlePageBackward = (key: PaginationKey) => {
    if (paginationState[key] > 1) {
      setPaginationState((prev) => ({
        ...prev,
        [key]: prev[key] - 1,
      }));
    }
  };

  const TabComponent = ({ value }: { value: string }) => {
    switch (value) {
      case 'info':
        return <DRepMetadata metadata={metadata} isOwner={isOwner} />;
      case 'votes':
        return (
          <DRepVotes
            votes={dRepVotes?.items || []}
            isOwner={isOwner}
            seoVotes={seoVotes}
            {...getContentState(dRepVotesQuery)}
            onRetry={dRepVotesQuery.refetch}
            paginationDetails={{
              currentPage: dRepVotes?.page || 0,
              size: dRepVotes?.size || 0,
              totalCount: dRepVotes?.totalCount || 0,
              onNext: () => {
                handlePageForward('votesPage');
              },
              onPrevious: () => {
                handlePageBackward('votesPage');
              },
            }}
          />
        );
      case 'active-delegators':
        return (
          <ActiveDRepDelegators
            delegators={ActiveDRepDelegatorsList?.items || []}
            isOwner={isOwner}
            {...getContentState(activeDRepDelegatorsListQuery)}
            onRetry={activeDRepDelegatorsListQuery.refetch}
            paginationDetails={{
              currentPage: ActiveDRepDelegatorsList?.page || 0,
              size: ActiveDRepDelegatorsList?.size || 0,
              totalCount: ActiveDRepDelegatorsList?.totalCount || 0,
              onNext: () => {
                handlePageForward('activeDelegatorsPage');
              },
              onPrevious: () => {
                handlePageBackward('activeDelegatorsPage');
              },
            }}
          />
        );
      case 'registrations':
        return (
          <DRepRegistrations
            registrations={dRepRegistrations?.items || []}
            isOwner={isOwner}
            {...getContentState(dRepRegistrationsQuery)}
            onRetry={dRepRegistrationsQuery.refetch}
            paginationDetails={{
              currentPage: dRepRegistrations?.page || 0,
              size: dRepRegistrations?.size || 0,
              totalCount: dRepRegistrations?.totalCount || 0,
              onNext: () => {
                handlePageForward('registrationsPage');
              },
              onPrevious: () => {
                handlePageBackward('registrationsPage');
              },
            }}
          />
        );
      case 'delegations':
        return (
          <Delegations
            delegations={dRepDelegations?.items || []}
            isOwner={isOwner}
            {...getContentState(dRepDelegationsQuery)}
            onRetry={dRepDelegationsQuery.refetch}
            paginationDetails={{
              currentPage: dRepDelegations?.page || 0,
              size: dRepDelegations?.size || 0,
              totalCount: dRepDelegations?.totalCount || 0,
              onNext: () => {
                handlePageForward('delegationsPage');
              },
              onPrevious: () => {
                handlePageBackward('delegationsPage');
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  const updateURLParam = (key: string, value: string) => {
    const currentParams = readSearchParams();
    currentParams.set(key, value);
    router.replace(`?${currentParams.toString()}`);
  };

  const handleSetActiveTab = (index: number) => {
    setActiveTab(dRepTabs[index]);
    updateURLParam('tab', dRepTabs[index].value);
  };

  useEffect(() => {
    const tab = readSearchParam('tab') || 'info';
    if (tab === 'info') {
      return;
    }
    const drepTab = dRepTabs.find((item) => item?.value === tab);
    drepTab && setActiveTab(drepTab);
    updateURLParam('tab', tab);
  }, []);

  return (
    <div className="rounded-lg border dark:border-dark-neutral-700 dark:bg-dark-neutral-900 lg:max-w-[655px] 2xl:max-w-full">
      <Tabs
        className="border-b border-gray-200 dark:border-dark-neutral-700"
        items={dRepTabs}
        onChange={handleSetActiveTab}
        currentActiveIndex={dRepTabs.findIndex(
          (tab) => tab.value === activeTab.value
        )}
      />

      {/*
        Every panel renders, with the inactive ones hidden rather than
        unmounted. All four queries already run on mount regardless of which
        tab is showing, and keeping the panels in the DOM is what puts the
        server-rendered votes into the initial HTML — a crawler never clicks
        the tab.
      */}
      {dRepTabs.map((tab) => (
        <div key={tab.value} hidden={tab.value !== activeTab.value}>
          <TabComponent value={tab.value} />
        </div>
      ))}
    </div>
  );
}
